import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { 
  BookOpen, Video, FileText, 
  Lock, Play, ChevronRight
} from "lucide-react";

const ContentLibrary = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/content`, { headers });
        setContent(res.data);
      } catch (err) {
        console.error("Failed to fetch content");
      }
      setLoading(false);
    };
    fetchContent();
  }, [token]);

  const categories = ["all", ...new Set(content.map(c => c.category))];
  const filteredContent = selectedCategory === "all" 
    ? content 
    : content.filter(c => c.category === selectedCategory);

  const getContentIcon = (type) => {
    switch (type) {
      case "video": return <Video className="h-5 w-5" />;
      case "pdf": return <FileText className="h-5 w-5" />;
      default: return <BookOpen className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="content-library-page">
      {/* Hero */}
      <div className="bg-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-400 mb-4">
            Resource Library
          </p>
          <h1 className="text-3xl md:text-4xl font-serif font-medium mb-4">
            Leadership Resources & Insights
          </h1>
          <p className="text-lg text-white/80 max-w-2xl">
            Access articles, videos, and guides to support your leadership development journey.
            {!user?.is_premium && " Upgrade to premium for full access to all content."}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Category Tabs */}
        <Tabs defaultValue="all" className="mb-8">
          <TabsList className="bg-white border border-stone-200">
            {categories.map(cat => (
              <TabsTrigger 
                key={cat} 
                value={cat}
                onClick={() => setSelectedCategory(cat)}
                className="capitalize"
                data-testid={`category-tab-${cat}`}
              >
                {cat}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-stone-200 rounded mb-4 w-3/4" />
                  <div className="h-4 bg-stone-200 rounded mb-2" />
                  <div className="h-4 bg-stone-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredContent.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No content available</h3>
            <p className="text-slate-600">Check back soon for new resources.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContent.map((item, index) => (
              <Card 
                key={item.id} 
                className="content-card group hover:border-orange-300 transition-colors"
                data-testid={`content-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-slate-600 group-hover:bg-orange-100 group-hover:text-orange-600 transition-colors">
                      {getContentIcon(item.content_type)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize text-xs">
                        {item.content_type}
                      </Badge>
                      {item.is_premium && (
                        <Badge className="premium-badge text-white text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Premium
                        </Badge>
                      )}
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs">
                      {item.category}
                    </Badge>
                    {item.is_premium && !user?.is_premium ? (
                      <Link to="/login" className="text-sm text-orange-600 font-medium flex items-center gap-1 hover:underline">
                        Unlock <ChevronRight className="h-4 w-4" />
                      </Link>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700">
                        {item.content_type === "video" ? (
                          <><Play className="h-4 w-4 mr-1" /> Watch</>
                        ) : (
                          <><BookOpen className="h-4 w-4 mr-1" /> Read</>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Premium CTA */}
        {!user?.is_premium && (
          <div className="mt-16 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 md:p-12 text-white text-center">
            <h3 className="text-2xl md:text-3xl font-serif font-medium mb-4">
              Unlock Premium Content
            </h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Get access to exclusive leadership resources, video masterclasses, and 
              downloadable frameworks with any coaching package.
            </p>
            <Link to="/#services">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-500">
                View Coaching Packages
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentLibrary;
